import React from "react";
import { PlusCircle, Settings, Loader, Building2, ChevronDown, Check } from "lucide-react";
import ReusableDropdown from "../../../../../components/ReusableDropdown";
import DatePicker from "../../../../../components/DatePicker";

const HolidayForm = ({
  theme, isRTL, t, language,
  holidayForm, onFormChange,
  editingHoliday, onCancelEdit,
  holidayCompanies,
  onSubmit, submitting,
  /* departments */
  onOpenDeptPicker,
}) => {
  const inputStyle = (extra = {}) => ({
    width: "100%",
    padding: "11px 14px",
    borderRadius: "10px",
    border: `1px solid ${theme === "dark" ? "rgba(255,255,255,0.15)" : "#d1d5db"}`,
    background: theme === "dark" ? "rgba(255,255,255,0.06)" : "#fff",
    color: theme === "dark" ? "#fff" : "#111827",
    fontSize: "14px",
    outline: "none",
    boxSizing: "border-box",
    ...extra,
  });

  const labelStyle = {
    fontSize: "12px", fontWeight: "700",
    color: theme === "dark" ? "rgba(255,255,255,0.7)" : "#374151",
    textTransform: "uppercase", letterSpacing: "0.5px",
    display: "block", marginBottom: "6px",
  };

  const companyItems = holidayCompanies.map((c) => {
    const name = typeof c === "string" ? c : (c.companyName || c.name || c.workingCompany || "");
    return { id: name, name };
  });

  /* Summarise selected departments for the button label */
  const selectedDepts = holidayForm.selectedDepartments || [];
  const allDepts = holidayForm.allDepartments === true;

  const hasWorkingCompany = !!holidayForm.workingCompany;

  const deptLabel = (() => {
    if (!hasWorkingCompany) return isRTL ? "اختر الشركة أولاً" : "Select a company first";
    if (allDepts) return isRTL ? "جميع الأقسام" : "All Departments";
    if (selectedDepts.length === 0) return isRTL ? "اختر الأقسام" : "Select Departments";
    if (selectedDepts.length === 1) return selectedDepts[0];
    return isRTL
      ? `${selectedDepts.length} أقسام مختارة`
      : `${selectedDepts.length} Departments`;
  })();

  const hasDeptSelection = allDepts || selectedDepts.length > 0;

  return (
    <div style={{
      background: theme === "dark" ? "rgba(255,255,255,0.04)" : "#f0fdf4",
      border: `1px solid ${theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(5,150,105,0.2)"}`,
      borderRadius: "16px",
      padding: "24px",
      marginBottom: "28px",
    }}>
      {/* Form title */}
      <div style={{ fontSize: "15px", fontWeight: "700", color: theme === "dark" ? "#34d399" : "#059669", marginBottom: "18px", display: "flex", alignItems: "center", gap: "8px" }}>
        {editingHoliday
          ? <><Settings size={16} /> {t.editHoliday}</>
          : <><PlusCircle size={16} /> {t.createHoliday}</>}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: "14px" }}>

        {/* Client — read-only */}
        <div>
          <label style={labelStyle}>{t.clientNameLabel} *</label>
          <div style={{
            padding: "11px 14px", borderRadius: "10px",
            background: theme === "dark" ? "rgba(255,255,255,0.06)" : "#f3f4f6",
            color: theme === "dark" ? "#d1d5db" : "#374151",
            fontSize: "14px", fontWeight: "600",
            border: `1px solid ${theme === "dark" ? "rgba(255,255,255,0.08)" : "#e5e7eb"}`,
            display: "flex", alignItems: "center", gap: "8px", minHeight: "42px",
          }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ opacity: 0.5, flexShrink: 0 }}>
              <rect x="3" y="11" width="18" height="11" rx="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            {holidayForm.clientName || (isRTL ? "لا يوجد عميل" : "No client")}
          </div>
        </div>

        {/* Working Company */}
        <div>
          <label style={labelStyle}>{t.workingCompanyLabel} *</label>
          <ReusableDropdown
            items={companyItems}
            value={holidayForm.workingCompany || ""}
            onChange={(selectedId) => onFormChange("workingCompany", selectedId)}
            placeholder={t.selectCompany}
            emptyMessage={t.noCompaniesFound}
            disabled={!holidayForm.clientName || !!editingHoliday}
            theme={theme}
            language={language}
            inputStyle={{
              ...inputStyle(),
              cursor: editingHoliday ? "not-allowed" : (holidayForm.clientName ? "pointer" : "not-allowed"),
              opacity: editingHoliday ? 0.6 : (holidayForm.clientName ? 1 : 0.5),
            }}
            showAllOnArrowClick={true}
            showClear={false}
          />
        </div>

        {/* Holiday Name */}
        <div>
          <label style={labelStyle}>{t.holidayName} *</label>
          <input
            type="text"
            value={holidayForm.name}
            onChange={(e) => onFormChange("name", e.target.value)}
            placeholder={t.holidayNamePlaceholder}
            disabled={!!editingHoliday}
            style={inputStyle({
              opacity: editingHoliday ? 0.6 : 1,
              cursor: editingHoliday ? "not-allowed" : "text",
            })}
          />
        </div>

        {/* Date */}
        <div>
          <label style={labelStyle}>{t.holidayDate} *</label>
          <DatePicker
            theme={theme}
            isRTL={isRTL}
            value={holidayForm.date}
            onChange={(v) => onFormChange("date", v)}
            buttonStyle={inputStyle()}
          />
        </div>

        {/* Departments picker trigger */}
        <div>
          <label style={labelStyle}>
            {isRTL ? "الأقسام" : "Departments"}
          </label>
          <button
            type="button"
            disabled={!hasWorkingCompany}
            onClick={onOpenDeptPicker}
            title={!hasWorkingCompany ? (isRTL ? "اختر الشركة أولاً لعرض الأقسام" : "Select a Working Company first to see its departments") : undefined}
            style={{
              /* Base — pixel-identical to every other input in this form */
              ...inputStyle(),
              /* Override the few things a button needs */
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "8px",
              cursor: hasWorkingCompany ? "pointer" : "not-allowed",
              opacity: hasWorkingCompany ? 1 : 0.5,
              textAlign: isRTL ? "right" : "left",
              transition: "border-color 0.15s ease, background 0.15s ease",
              /* Accent overrides only when something is selected */
              ...(hasDeptSelection && {
                border: `1px solid ${theme === "dark" ? "rgba(52,211,153,0.45)" : "rgba(5,150,105,0.4)"}`,
                background: theme === "dark" ? "rgba(52,211,153,0.07)" : "rgba(5,150,105,0.05)",
              }),
              color: hasDeptSelection
                ? (theme === "dark" ? "#34d399" : "#059669")
                : (theme === "dark" ? "rgba(255,255,255,0.35)" : "#9ca3af"),
              fontWeight: hasDeptSelection ? "600" : "400",
            }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: "8px", overflow: "hidden" }}>
              <Building2 size={15} style={{ flexShrink: 0, opacity: hasDeptSelection ? 1 : 0.5 }} />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {deptLabel}
              </span>
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: "4px", flexShrink: 0 }}>
              {hasDeptSelection && (
                <span style={{
                  background: theme === "dark" ? "#34d399" : "#059669",
                  color: "#fff",
                  borderRadius: "999px",
                  fontSize: "10px",
                  fontWeight: "700",
                  padding: "1px 7px",
                  lineHeight: "18px",
                }}>
                  {allDepts ? (isRTL ? "الكل" : "All") : selectedDepts.length}
                </span>
              )}
              <ChevronDown size={14} style={{ opacity: 0.5 }} />
            </span>
          </button>
        </div>

      </div>

      {/* Actions */}
      <div style={{ display: "flex", gap: "12px", marginTop: "20px", justifyContent: "flex-end" }}>
        {editingHoliday && (
          <button
            onClick={onCancelEdit}
            style={{ padding: "10px 22px", borderRadius: "10px", border: `1px solid ${theme === "dark" ? "rgba(255,255,255,0.15)" : "#d1d5db"}`, background: "transparent", color: theme === "dark" ? "#fff" : "#374151", fontSize: "14px", fontWeight: "600", cursor: "pointer" }}
          >
            {t.cancel}
          </button>
        )}
        <button
          onClick={onSubmit}
          disabled={submitting}
          style={{ padding: "10px 28px", borderRadius: "10px", border: "none", background: submitting ? "#6b7280" : "linear-gradient(135deg,#059669,#047857)", color: "#fff", fontSize: "14px", fontWeight: "700", cursor: submitting ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: "8px", boxShadow: "0 4px 14px rgba(5,150,105,0.3)" }}
        >
          {submitting
            ? <><Loader size={16} style={{ animation: "spin 1s linear infinite" }} />{t.loading}</>
            : (editingHoliday ? t.saveChanges : t.createHoliday)}
        </button>
      </div>
    </div>
  );
};

export default HolidayForm;